/**
 * ルーム（ゲームサーバー。docs/game-server.md・ocgcore/README.md「ルーム（room/）」）の WebSocket の殻（Issue #765。索引 #761）。
 * つなぐ・切る・つなぎ直す・ポーリングに戻るの判断は `nativeBridge/roomLink.js` の状態遷移表（iPad と同じもの）で、ここは返った actions を実行するだけ。
 *
 * 使い方（pages/OcgInput.js の席モード・見る専用）:
 *   const link = new RoomLink({ onState, onProgress, onApply, onReload, onNotice, onChange });
 *   link.seat(v)            本番の OcgSeatState の返り（seatView の形。room が付いていればつなぎ替える。無ければ onApply(v)＝今までどおり）
 *   link.needsPoll()        本番を読むか（poll と再接続中）。pollDelay(ms) は次に読むまで
 *   await link.answer({...}) / undo / progress / skip / catchup / policy / close → { ok, status, body, room: true, ack }
 *     body は書いたあとの最新の state（ルームが全員へ送ったもの。onState には先に届いている）、失敗は本番の書き込みと同じ形（body.error / stale / force_required）
 *   link.destroy()
 */
import {
  answerMessage, initialLink, needsPoll, pollDelay, progressMessage, roomStatus, roomStep, undoMessage, wsUrl,
} from '../../nativeBridge/roomLink';

/** 殻の時計（黙ったつなぎ先を切る・ping）の間隔 */
export const ROOM_TICK_MS = 5000;

const defaultBase = () => {
  const env = (typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL) || '';
  if (env) return env;
  return typeof window !== 'undefined' && window.location ? window.location.origin : '';
};

export class RoomLink {
  constructor({ base = null, WebSocketImpl = null, now = null, tickMs = ROOM_TICK_MS,
    onState = null, onProgress = null, onApply = null, onReload = null, onNotice = null, onChange = null } = {}) {
    this.base = base ?? defaultBase();
    this.WebSocketImpl = WebSocketImpl ?? (typeof WebSocket !== 'undefined' ? WebSocket : null);
    this.now = now ?? (() => Date.now());
    this.cb = { onState, onProgress, onApply, onReload, onNotice, onChange };
    this.link = initialLink();
    this.ws = null;
    this.pending = new Map();
    this.nextReq = 1;
    this.lastState = null;
    this.timer = tickMs > 0 ? setInterval(() => this.dispatch({ type: 'tick' }), tickMs) : null;
  }

  get status() { return roomStatus(this.link); }
  needsPoll() { return needsPoll(this.link); }
  pollDelay(ms) { return pollDelay(this.link, ms); }

  dispatch(event) {
    const before = roomStatus(this.link);
    const { link, actions } = roomStep(this.link, { ...event, now: this.now() });
    this.link = link;
    actions.forEach((a) => this._run(a));
    const after = roomStatus(this.link);
    if (after.phase !== before.phase || after.label !== before.label) this.cb.onChange?.(after);
  }

  /** 本番の OcgSeatState の返り（seatView の形） */
  seat(v) { this.dispatch({ type: 'seat', body: v }); }

  request(msg) {
    const req = this.nextReq;
    this.nextReq += 1;
    const p = new Promise((resolve) => this.pending.set(req, resolve));
    this.dispatch({ type: 'request', req, msg });
    return p;
  }

  answer(opts) { return this.request(answerMessage(opts)); }
  undo(opts) { return this.request(undoMessage(opts)); }
  progress(opts) { return this.request(progressMessage(opts)); }
  skip() { return this.request({ type: 'skip' }); }
  catchup(on) { return this.request({ type: 'catchup', on: !!on }); }
  policy(policy) { return this.request({ type: 'policy', policy }); }
  close() { return this.request({ type: 'close' }); }

  destroy() {
    this.dispatch({ type: 'leave' });
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  _run(a) {
    switch (a.do) {
      case 'connect': this._open(a.url, a.conn); break;
      case 'disconnect': {
        const ws = this.ws;
        this.ws = null;
        try { ws?.close(); } catch (e) { /* もう閉じている */ }
        break;
      }
      case 'send':
        if (this.ws && this.ws.readyState === 1) {
          try { this.ws.send(JSON.stringify(a.msg)); } catch (e) { /* 閉じかけ。socketClosed で拾う */ }
        }
        break;
      case 'apply': this.cb.onApply?.(a.body); break;
      case 'state': this.lastState = a.msg; this.cb.onState?.(a.msg); break;
      case 'progress': this.cb.onProgress?.(a.progress); break;
      case 'resolve': this._resolve(a.req, a.result); break;
      case 'rejectAll': [...this.pending.keys()].forEach((req) => this._resolve(req, a.result)); break;
      case 'reload': this.lastState = null; this.cb.onReload?.(); break;
      case 'notice': this.cb.onNotice?.(a.text); break;
      default: break;
    }
  }

  _resolve(req, result) {
    const fn = this.pending.get(req);
    if (!fn) return;
    this.pending.delete(req);
    fn(result.ok ? { ...result, ack: result.body, body: this.lastState } : result);
  }

  _open(url, conn) {
    if (!this.WebSocketImpl) { this.dispatch({ type: 'socketClosed', conn }); return; }
    let ws;
    try { ws = new this.WebSocketImpl(wsUrl(url, this.base)); } catch (e) { setTimeout(() => this.dispatch({ type: 'socketClosed', conn }), 0); return; }
    this.ws = ws;
    ws.onopen = () => this.dispatch({ type: 'open', conn });
    ws.onmessage = (ev) => {
      let msg = null;
      try { msg = JSON.parse(typeof ev.data === 'string' ? ev.data : String(ev.data)); } catch (e) { return; }
      this.dispatch({ type: 'message', msg, conn });
    };
    ws.onclose = () => this.dispatch({ type: 'socketClosed', conn });
    ws.onerror = () => { /* onclose が続く */ };
  }
}
